import { useSelector, useDispatch } from 'react-redux';
import { conversationsActions } from '@actions';
import { conversationsService } from '@services';
import { handleApiError } from '@helpers/api';
import { useConversations, userCoversTeam } from '@hooks';

// Removing yourself from a conversation whose team you can't access drops it entirely — the grant was your only way in.
export const useCollaborators = ({ setActiveConversation, filterRef }) => {
    const currentUser = useSelector(state => state.auth.user);

    const dispatch = useDispatch();
    const { updateConversationInFeedAndReturn, upsertConversationInFeed, removeConversationById, conversationsRef } =
        useConversations();

    const syncConversation = conversation => {
        setActiveConversation(prev => (prev?.id === conversation.id ? { ...prev, ...conversation } : prev));
        updateConversationInFeedAndReturn(conversation, filterRef.current, true);
    };

    const patchCollaborators = (conversationId, mutateIds) => {
        const previousRow = conversationsRef.current.find(c => c.id === conversationId);
        if (previousRow) {
            updateConversationInFeedAndReturn(
                { ...previousRow, collaborators: mutateIds(previousRow.collaborators || []) },
                filterRef.current,
                true,
            );
        }
        setActiveConversation(prev =>
            prev?.id === conversationId ? { ...prev, collaborators: mutateIds(prev.collaborators || []) } : prev,
        );
        return previousRow;
    };

    const rollbackCollaborators = (conversationId, previousRow, restoreIds) => {
        const currentRow = conversationsRef.current.find(c => c.id === conversationId);
        if (currentRow) {
            updateConversationInFeedAndReturn(
                { ...currentRow, collaborators: restoreIds(currentRow.collaborators || []) },
                filterRef.current,
                true,
            );
        } else if (previousRow) {
            upsertConversationInFeed(previousRow, filterRef.current);
        }
        setActiveConversation(prev =>
            prev?.id === conversationId ? { ...prev, collaborators: restoreIds(prev.collaborators || []) } : prev,
        );
        dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
    };

    const addCollaborator = async (conversationId, userId) => {
        const previousRow = patchCollaborators(conversationId, ids => [...ids.filter(id => id !== userId), userId]);
        try {
            const conversation = await conversationsService.addCollaborator(conversationId, userId);
            syncConversation(conversation);
        } catch (error) {
            rollbackCollaborators(conversationId, previousRow, ids => ids.filter(id => id !== userId));
            handleApiError(error);
        }
    };

    const removeCollaborator = async (conversationId, userId) => {
        const removedSelf = userId === currentUser?.id;
        if (removedSelf) {
            try {
                const conversation = await conversationsService.removeCollaborator(conversationId, userId);
                if (!userCoversTeam(currentUser, conversation.teamId)) {
                    setActiveConversation(null);
                    removeConversationById(conversationId);
                    dispatch(conversationsActions.getCounters(filterRef.current));
                    return;
                }
                syncConversation(conversation);
            } catch (error) {
                handleApiError(error);
            }
            return;
        }

        const previousRow = patchCollaborators(conversationId, ids => ids.filter(id => id !== userId));
        try {
            const conversation = await conversationsService.removeCollaborator(conversationId, userId);
            syncConversation(conversation);
        } catch (error) {
            rollbackCollaborators(conversationId, previousRow, ids => [...ids.filter(id => id !== userId), userId]);
            handleApiError(error);
        }
    };

    return { addCollaborator, removeCollaborator };
};
